import { CdkDrag } from '@angular/cdk/drag-drop';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, InjectionToken, Injector, OnDestroy, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { GridModalComponent, GridModalRegistry } from '@app/core/components/grid/grid.decorators';
import { TranslateService } from '@app/core/services/translate.service';
import { GotoParms, StateService as TTStateService } from '@app/core/services/state.service';
import { StateService } from '@uirouter/angularjs';
import { StateModalPosition, StateModalSizeService } from './state-modal-size.service';
import { Subscription } from 'rxjs';

@GridModalComponent('StateModal')
@Component({
    selector: 'tt-state-modal',
    templateUrl: './state-modal.component.html',
    styleUrls: ['./state-modal.component.css'],
    standalone: false,
    encapsulation: ViewEncapsulation.None,
})
export class StateModalComponent implements AfterViewInit, OnDestroy {
    @ViewChild(CdkDrag)
    private dialogDrag?: CdkDrag;

    public heading = '';

    public component;

    public injector: any;

    private defaultSize: { width: string; height: string };

    private resizeState?: StateModalResizeState;

    private removePointerMoveListener?: () => void;

    private removePointerUpListener?: () => void;

    private removePointerCancelListener?: () => void;

    private keyboardRememberTimeout?: number;

    private usingDefaultSize: boolean;

    private navigationSubscription?: Subscription;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: StateModalData,
        public dialogRef: MatDialogRef<StateModalComponent>,
        injector: Injector,
        private cdr: ChangeDetectorRef,
        private state: StateService,
        private ttState: TTStateService,
        private translate: TranslateService,
        public navbar: NavBarService,
        private stateModalSize: StateModalSizeService,
        private elementRef: ElementRef<HTMLElement>,
        private renderer: Renderer2,
    ) {
        dialogRef.disableClose = true;
        this.defaultSize = {
            width: data.width ?? '',
            height: data.height ?? '',
        };
        this.usingDefaultSize = !data.hasRememberedSize;
        // data.state
        // this.getParams(data);

        this.component = GridModalRegistry.get(data.state);

        if (!this.component) {
            // @ts-ignore
            this.component = state.get(data.state)?.component;
        }

        if (!this.component) {
            dialogRef.close();
        }

        this.component.params = this.getParams(data);
        // this.component.params = { project_keyno: data.params };
        this.component?.submit?.subscribe({ next: () => this.dialogRef.close() });
        this.cdr.markForCheck();
        this.injector = Injector.create({
            providers: [
                {
                    provide: TT_STATE_DIALOG,
                    useValue: <StateDialogContext>{
                        params: this.getParams(data),
                        // params: { project_keyno: data.params },
                        close: (params) => this.dialogRef.close(params),
                        setHeading: async (heading, translate) => {
                            if (translate) {
                                this.heading = await this.translate.translate(heading);
                            } else {
                                this.heading = heading;
                            }
                        },
                        setSize: (params) => {
                            this.setDefaultSize(params);
                        },
                        dialogRef: dialogRef,
                    },
                },
                {
                    provide: TT_VIEW_NAVIGATOR,
                    useValue: <TTViewNavigator>{
                        params: this.getParams(data),
                        host: 'dialog',
                        owner: this.component,
                        back: (to, result) => {
                            this.dialogRef.close(result);
                        },

                        dismiss: (result?: any) => {
                            this.dialogRef.close(<DialogNavResult>{ kind: 'dismiss', result });
                        },

                        go: (target: TTNavTarget) => {
                            this.ttState.go(target.to, target.parms, target.options);
                            this.dialogRef.close(<DialogNavResult>{ kind: 'go', target });
                        },
                        newTab: (target: TTNavTarget) => {
                            this.ttState.newTab(target.to, target.parms);
                        },
                        sequence: async (targets: TTNavTarget[]) => {
                            // for (const target of targets) {
                            //     await this.ttState.go(target.to, target.parms, target.options);
                            // }
                            this.dialogRef.close(<DialogNavResult>{ kind: 'sequence', targets });
                        },

                        setHeading: async (heading: string, translate?: boolean) => {
                            if (translate) {
                                this.heading = await this.translate.translate(heading);
                            } else {
                                this.heading = heading;
                            }
                        },

                        setSize: (params: Partial<{ width: string; height: string }>) => {
                            console.log(this);
                            this.setDefaultSize(params);
                        },
                    },
                },
            ],
            parent: injector,
        });
        if (data.closeOnNavigation !== false) {
            this.navigationSubscription = this.ttState.navigationSucceeded$.subscribe(() => {
                if (this.dialogRef.getState() === MatDialogState.OPEN) {
                    this.dialogRef.close();
                }
            });
        }
        console.dir(this.component);
    }

    public ngAfterViewInit(): void {
        if (this.data.position) {
            this.clampDialogPosition(this.data.position);
        }
    }

    public startResize(event: PointerEvent): void {
        if (event.button !== 0 || this.resizeState) return;

        const pane = this.getDialogPane();
        const boundary = pane?.closest('.cdk-overlay-container') as HTMLElement | null | undefined;

        if (!pane || !boundary) return;

        event.preventDefault();
        event.stopPropagation();

        const paneRect = pane.getBoundingClientRect();
        const boundaryRect = boundary.getBoundingClientRect();
        const handle = event.currentTarget as HTMLElement;

        this.anchorDialog(paneRect, boundaryRect);
        handle.setPointerCapture(event.pointerId);

        this.resizeState = {
            pointerId: event.pointerId,
            handle,
            startX: event.clientX,
            startY: event.clientY,
            startWidth: paneRect.width,
            startHeight: paneRect.height,
            left: paneRect.left - boundaryRect.left,
            top: paneRect.top - boundaryRect.top,
            boundaryWidth: boundaryRect.width,
            boundaryHeight: boundaryRect.height,
        };

        this.removePointerMoveListener = this.renderer.listen('document', 'pointermove', (pointerEvent: PointerEvent) => this.onPointerMove(pointerEvent));
        this.removePointerUpListener = this.renderer.listen('document', 'pointerup', (pointerEvent: PointerEvent) => this.finishResize(pointerEvent));
        this.removePointerCancelListener = this.renderer.listen('document', 'pointercancel', (pointerEvent: PointerEvent) => this.finishResize(pointerEvent));
    }

    public resizeWithKeyboard(event: KeyboardEvent): void {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;

        const pane = this.getDialogPane();
        const boundary = pane?.closest('.cdk-overlay-container') as HTMLElement | null | undefined;

        if (!pane || !boundary) return;

        event.preventDefault();
        event.stopPropagation();

        const paneRect = pane.getBoundingClientRect();
        const boundaryRect = boundary.getBoundingClientRect();
        const step = event.shiftKey ? 50 : 10;
        const widthDelta = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
        const heightDelta = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;

        this.anchorDialog(paneRect, boundaryRect);
        this.updateDialogSize(
            paneRect.width + widthDelta,
            paneRect.height + heightDelta,
            paneRect.left - boundaryRect.left,
            paneRect.top - boundaryRect.top,
            boundaryRect.width,
            boundaryRect.height,
        );
        this.usingDefaultSize = false;
        this.scheduleKeyboardRemember();
    }

    public rememberPosition(): void {
        this.persistLayout();
    }

    public resetSize(): void {
        this.cancelActiveResize(false);
        this.clearKeyboardRememberTimeout();
        this.data.hasRememberedSize = false;
        this.data.position = undefined;
        this.usingDefaultSize = true;

        this.dialogDrag?.reset();
        this.dialogRef.updateSize(this.defaultSize.width, this.defaultSize.height);
        this.dialogRef.updatePosition();

        void this.stateModalSize.rememberDefault(this.data.state).catch((error) => console.error(error));
    }

    public ngOnDestroy(): void {
        this.navigationSubscription?.unsubscribe();

        const shouldPersistSize = this.resizeState !== undefined || this.keyboardRememberTimeout !== undefined;

        this.cancelActiveResize(false);
        this.clearKeyboardRememberTimeout();

        if (shouldPersistSize) {
            this.persistLayout();
        }
    }

    private setDefaultSize(params: Partial<{ width: string; height: string }>): void {
        if (this.data.width || this.data.height) return;

        if (params.width !== undefined) {
            this.defaultSize.width = params.width;
        }

        if (params.height !== undefined) {
            this.defaultSize.height = params.height;
        }

        if (!this.data.hasRememberedSize) {
            this.dialogRef.updateSize(params.width, params.height);
        }
    }

    private onPointerMove(event: PointerEvent): void {
        const resize = this.resizeState;

        if (!resize || resize.pointerId !== event.pointerId) return;

        event.preventDefault();

        if (event.clientX !== resize.startX || event.clientY !== resize.startY) {
            resize.resized = true;
        }

        this.updateDialogSize(
            resize.startWidth + event.clientX - resize.startX,
            resize.startHeight + event.clientY - resize.startY,
            resize.left,
            resize.top,
            resize.boundaryWidth,
            resize.boundaryHeight,
        );
    }

    private finishResize(event: PointerEvent): void {
        const resize = this.resizeState;

        if (!resize || resize.pointerId !== event.pointerId) return;

        this.usingDefaultSize = !resize.resized && this.usingDefaultSize;
        this.cancelActiveResize(resize.resized === true);
    }

    private updateDialogSize(width: number, height: number, left: number, top: number, boundaryWidth: number, boundaryHeight: number): void {
        const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const minWidth = Math.min(35 * rootFontSize, boundaryWidth);
        const minHeight = Math.min(20 * rootFontSize, boundaryHeight);
        const maxWidth = Math.max(minWidth, boundaryWidth - left);
        const maxHeight = Math.max(minHeight, boundaryHeight - top);
        const clampedWidth = Math.min(Math.max(width, minWidth), maxWidth);
        const clampedHeight = Math.min(Math.max(height, minHeight), maxHeight);

        this.dialogRef.updateSize(`${Math.round(clampedWidth)}px`, `${Math.round(clampedHeight)}px`);
    }

    private anchorDialog(paneRect: DOMRect, boundaryRect: DOMRect): void {
        this.dialogRef.updateSize(`${Math.round(paneRect.width)}px`, `${Math.round(paneRect.height)}px`);
        this.dialogRef.updatePosition({
            left: `${Math.round(paneRect.left - boundaryRect.left)}px`,
            top: `${Math.round(paneRect.top - boundaryRect.top)}px`,
        });
        this.dialogDrag?.reset();
    }

    private persistLayout(): void {
        const rect = this.getDialogPane()?.getBoundingClientRect();
        const position = this.getCurrentPosition();

        if (!rect || !position) return;

        const promise = this.usingDefaultSize
            ? this.stateModalSize.rememberDefault(this.data.state, position)
            : this.stateModalSize.rememberSize(this.data.state, Math.round(rect.width), Math.round(rect.height), position);

        void promise.catch((error) => console.error(error));
    }

    private scheduleKeyboardRemember(): void {
        this.clearKeyboardRememberTimeout();
        this.keyboardRememberTimeout = window.setTimeout(() => {
            this.keyboardRememberTimeout = undefined;
            this.persistLayout();
        }, 500);
    }

    private clearKeyboardRememberTimeout(): void {
        if (this.keyboardRememberTimeout !== undefined) {
            window.clearTimeout(this.keyboardRememberTimeout);
            this.keyboardRememberTimeout = undefined;
        }
    }

    private cleanupPointerListeners(): void {
        this.removePointerMoveListener?.();
        this.removePointerUpListener?.();
        this.removePointerCancelListener?.();
        this.removePointerMoveListener = undefined;
        this.removePointerUpListener = undefined;
        this.removePointerCancelListener = undefined;
    }

    private cancelActiveResize(persistSize: boolean): void {
        const resize = this.resizeState;

        if (resize?.handle.hasPointerCapture(resize.pointerId)) {
            resize.handle.releasePointerCapture(resize.pointerId);
        }

        this.resizeState = undefined;
        this.cleanupPointerListeners();

        if (persistSize) {
            this.persistLayout();
        }
    }

    private getCurrentPosition(): StateModalPosition | undefined {
        const pane = this.getDialogPane();
        const boundary = pane?.closest('.cdk-overlay-container') as HTMLElement | null | undefined;

        if (!pane || !boundary) return undefined;

        const paneRect = pane.getBoundingClientRect();
        const boundaryRect = boundary.getBoundingClientRect();

        return {
            x: Math.round(paneRect.left - boundaryRect.left),
            y: Math.round(paneRect.top - boundaryRect.top),
        };
    }

    private clampDialogPosition(position: StateModalPosition): void {
        const pane = this.getDialogPane();
        const boundary = pane?.closest('.cdk-overlay-container') as HTMLElement | null | undefined;

        if (!pane || !boundary) return;

        const paneRect = pane.getBoundingClientRect();
        const boundaryRect = boundary.getBoundingClientRect();
        const x = Math.min(Math.max(position.x, 0), Math.max(0, boundaryRect.width - paneRect.width));
        const y = Math.min(Math.max(position.y, 0), Math.max(0, boundaryRect.height - paneRect.height));

        this.dialogRef.updatePosition({ left: `${Math.round(x)}px`, top: `${Math.round(y)}px` });
    }

    private getDialogPane(): HTMLElement | undefined {
        return (this.elementRef.nativeElement.closest('.cdk-overlay-pane') as HTMLElement | null) ?? undefined;
    }

    private getParams(data: any) {
        // TODO: fix that params query is sent to.

        const params: { [key: string]: string } = {};

        const [path = '', query = ''] = data.params?.split('?') ?? [];
        const values = path.split('/');
        console.log(this.state.get(data.state));
        console.log(this.state.get(data.state).params);

        const paramsKeys =
            this.state
                .get(data.state)
                .url?.split('?')[0]
                .split('/')
                .filter((part) => part.startsWith(':'))
                .map((part) => part.replace(':', '')) ?? [];

        for (let i = 0; i < paramsKeys.length; i++) {
            params[paramsKeys[i]] = values[i];
        }

        new URLSearchParams(query).forEach((value, key) => {
            params[key] = value || 'true';
        });

        return params;
    }
}

export interface StateModalData {
    state: string;
    params?: string;
    width?: string;
    height?: string;
    hasRememberedSize?: boolean;
    closeOnNavigation?: boolean;
    position?: StateModalPosition;
}

interface StateModalResizeState {
    pointerId: number;
    handle: HTMLElement;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    left: number;
    top: number;
    boundaryWidth: number;
    boundaryHeight: number;
    resized?: boolean;
}

export const TT_STATE_DIALOG = new InjectionToken<StateDialogContext>('TT_STATE_DIALOG');

export interface StateDialogContext {
    params: { [key: string]: string };
    close(params?: any): void;
    setHeading(heading: string, translate?: boolean): void;
    setSize(params: Partial<{ width: string; height: string }>): void;
    dialogRef: MatDialogRef<unknown, unknown>;
}

// tt-view-navigator.ts

export interface TTNavTarget {
    to: string;
    parms?: GotoParms<unknown>;
    options?: TransitionOptions;
}

export interface TTViewNavigator {
    params: Record<string, unknown>;
    host: 'page' | 'dialog' | 'popup';
    owner: ComponentType<unknown>;

    /**
     * Goes back to the previous state if no `to` is given. If given a number, will jump back the given number in the state chain.
     * If given a string will go back to the given statechain path (like `'/workdetails/605000'`), and back to the mainmenu if the path is not found.
     *
     * @param to the number of steps to jump back in the state chain, or the state chain path to go back to.
     */
    back(to?: string | number | undefined, result?: unknown): Promise<void> | void;
    dismiss(result?: unknown): Promise<void> | void;
    go(target: TTNavTarget): Promise<void> | void;
    newTab(target: TTNavTarget): Promise<void> | void;
    sequence(targets: TTNavTarget[]): Promise<void> | void;
    setHeading?(heading: string, translate?: boolean): void;
    setSize?(size: Partial<{ width: string; height: string }>): void;
}

export const TT_VIEW_NAVIGATOR = new InjectionToken<TTViewNavigator>('TT_VIEW_NAVIGATOR');

import { Transition, TransitionOptions } from '@uirouter/core';
import { NavBarService } from '@app/core/components/nav-bar/nav-bar.service';
import { ComponentType } from '@angular/cdk/portal';

export function createPageNavigator(trans: Transition, state: TTStateService, translation: TranslateService, owner: ComponentType<unknown>): TTViewNavigator {
    const go = (target: TTNavTarget) => {
        state.go(target.to, target.parms || {}, target.options);
    };

    const newTab = (target: TTNavTarget) => {
        state.newTab(target.to, target.parms || {});
    };

    return {
        params: trans.params(),
        host: 'page',
        owner,

        back: (to?: any) => {
            state.back(to);
        },
        dismiss: () => {
            state.back();
        },

        go,
        newTab,
        sequence: async (targets) => {
            for (const target of targets) {
                await go(target);
            }
        },

        setHeading: async (heading: string, translate?: boolean) => {
            // window.document.title = heading;

            const pageHeading = document.querySelector('tt-page-heading');

            if (!!pageHeading) {
                if (translate == true) {
                    try {
                        heading = await translation.translate(heading);
                    } catch (error) {
                        console.error(error);
                    }
                }

                pageHeading.querySelector('span')!.innerText = heading;
            }

            state.setTitleOverride(heading, false);
        },
        setSize: () => {},
    };
}

export function createPopupNavigator(params: { [key: string]: any }, state: TTStateService, translation: TranslateService, win: Window, owner: ComponentType<unknown>): TTViewNavigator {
    const go = (target: TTNavTarget) => state.go(target.to, target.parms || {}, target.options);
    const newTab = (target: TTNavTarget) => state.newTab(target.to, target.parms || {});

    return {
        params,
        host: 'popup',
        owner,

        back: (to?: any, result?: unknown) => {
            window.opener.postMessage({
                type: 'ttData',
                data: result,
            });

            setTimeout(() => {
                window.close();
            });
            // win.close();
        },

        dismiss: () => {
            win.close();
        },

        go,
        newTab,
        sequence: async (targets) => {
            for (const target of targets) {
                await go(target);
            }
        },

        setHeading: async (heading, translate) => {
            const pageHeading = document.querySelector('tt-page-heading');

            if (!!pageHeading) {
                if (translate == true) {
                    try {
                        heading = await translation.translate(heading);
                    } catch (error) {
                        console.error(error);
                    }
                }
                pageHeading.querySelector('span')!.innerText = heading;
            }

            state.setTitleOverride(heading, false);
        },

        setSize: () => {},
    };
}

export type DialogNavResult = { kind: 'back'; result?: any } | { kind: 'dismiss'; result?: any } | { kind: 'go'; target: TTNavTarget } | { kind: 'sequence'; targets: TTNavTarget[] };

export function useNavigatorFor<T>(navigator: TTViewNavigator | null | undefined, owner: T): TTViewNavigator | undefined {
    if (!navigator) return undefined;

    if (typeof owner === 'object' && owner && typeof navigator.owner === 'function') {
        return owner instanceof navigator.owner ? navigator : undefined;
    }

    return owner === navigator.owner ? navigator : undefined;
}
